/**
 * port_window.ts — the PORT RUN window (space campaign E, 2026-09-30): one themed window, three views.
 *
 *   terms    before you take a run: every term named separately (lib/port_terms.ts termsLines), the crew you name and
 *            their shares of the ONE reward (a live preview of what delivery pays), the pre-authorised relay partner;
 *            Accept is the only way to take it (the spaceport's E and the dock's "Terms…").
 *   run      a run in your hold (the captain): the frozen terms, offer it to a pilot NOW (ceding part of your own
 *            share), revoke; or a run you may take over (the receiver): "Take over" — at once docked at one station,
 *            a held transfer in flight (begin, hold beside the hull, complete).
 *   receipt  after a delivery: the receipt chain, link by link, with the specific reason where it stands still.
 *
 * Every action is a reducer; a refusal is printed in the window and toasted (the server's own words). Text + icon,
 * never colour alone; tokens only (THE TOKEN LAW); it fits 720p at 200 %.
 */
import { makeWindow, type UiWindow } from './ui_window.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { termsLines, receiptLines, splitPreview, bpsOf, pct, sov, type TermsLine } from './port_terms.js';
import { crewTermsProblem, RELAY_HOLD_S, RELAY_OFFER_S, type ShareIn } from '@shared/space_economy.js';
import {
  ensurePortRows, run, termsOf, termsInputFor, crewOf, nameOf, pilotByName, me, isZero, ZERO, crewCandidates, relayRole,
  receipts, receiptView, myReceipts, type HaulRow,
} from './port_client.js';
import { Identity } from 'spacetimedb';

type View = { kind: 'terms'; haulId: bigint } | { kind: 'run'; haulId: bigint } | { kind: 'receipt'; haulId: bigint } | { kind: 'receipts' };
interface Reducers {
  portAccept(a: { haulId: bigint }): Promise<void>;
  portAcceptTerms(a: { haulId: bigint; crew: Array<{ who: Identity; shareBps: number }>; relayPartner: Identity }): Promise<void>;
  portRelayOffer(a: { haulId: bigint; to: Identity; shareBps: number }): Promise<void>;
  portRelayRevoke(a: { haulId: bigint }): Promise<void>;
  portRelay(a: { haulId: bigint }): Promise<void>;
}
const R = (): Reducers | null => (atlas.conn ? (atlas.conn.reducers as unknown as Reducers) : null);
const css = {
  row: 'display:grid;grid-template-columns:minmax(110px, 30%) 1fr;gap:4px 10px;align-items:baseline',
  label: 'font:600 11px var(--ax-mono);color:var(--ax-muted)',
  text: 'font:12px var(--ax-font);color:var(--ax-text);line-height:1.35',
  warn: 'font:12px var(--ax-font);color:var(--ax-warn);line-height:1.35',
  head: 'font:700 11px var(--ax-mono);color:var(--ax-hi);letter-spacing:1px;margin-top:6px',
  input: 'box-sizing:border-box;background:var(--ax-bg2);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:4px 6px;font:12px var(--ax-mono);min-height:28px',
  btn: 'background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:5px 12px;cursor:pointer;font:12px var(--ax-mono);min-height:30px',
  primary: 'background:var(--ax-accent);border:1px solid var(--ax-accent);border-radius:var(--ax-radius);color:var(--ax-on-accent);padding:5px 14px;cursor:pointer;font:700 12px var(--ax-mono);min-height:30px',
};

class PortWindow {
  private win: UiWindow | null = null;
  private view: View = { kind: 'receipts' };
  private crew: Array<{ name: string; pct: string }> = [];
  private partner = '';
  private offerName = '';
  private offerPct = '0';
  private status = '';
  private timer = 0;
  private holdUntil = 0;

  open(view: View): void {
    ensurePortRows();
    if (this.view.kind !== view.kind || ('haulId' in view && 'haulId' in this.view && view.haulId !== this.view.haulId)) {
      this.crew = []; this.partner = ''; this.offerName = ''; this.offerPct = '0'; this.status = '';
    }
    this.view = view;
    if (!this.win) {
      this.win = makeWindow({ id: 'port-run', title: 'Port run', icon: 'menu', width: 620, minWidth: 320, minHeight: 200, onClose: () => this.stop() });
      this.win.root.dataset.portWindow = '1';
    }
    this.win.root.style.maxWidth = 'calc(100vw - 16px)';
    this.win.root.style.maxHeight = 'calc(100vh - 16px)';
    this.win.setOpen(true);
    if (document.pointerLockElement) document.exitPointerLock?.();
    if (!this.resizeHooked) { this.resizeHooked = true; window.addEventListener('resize', () => this.fitIntoView()); }
    this.render();
    window.clearInterval(this.timer);
    this.timer = window.setInterval(() => { if (!this.editing()) this.render(); }, 1000);
  }
  close(): void { this.win?.setOpen(false); this.stop(); }
  isOpen(): boolean { return !!this.win?.isOpen(); }
  current(): View { return this.view; }
  private stop(): void { window.clearInterval(this.timer); this.timer = 0; }
  private editing(): boolean { const a = document.activeElement; return !!(a && this.win?.root.contains(a) && (a.tagName === 'INPUT' || a.tagName === 'SELECT')); }

  private say(text: string, kind: 'error' | 'info' | 'success' = 'info'): void {
    this.status = text;
    bus.emit('atlas:toast', { text, kind } as never);
    this.render();
  }
  private async call(p: Promise<void> | undefined, ok: string): Promise<boolean> {
    if (!p) { this.say('Not connected', 'error'); return false; }
    try { await p; this.say(ok, 'success'); return true; } catch (e) { this.say(String((e as Error)?.message ?? e), 'error'); return false; }
  }

  private render(): void {
    if (!this.win) return;
    const body = this.win.body;
    body.replaceChildren();
    body.style.cssText = 'display:flex;flex-direction:column;gap:6px;padding:10px 12px;overflow-y:auto;box-sizing:border-box';
    const v = this.view;
    if (v.kind === 'terms') this.renderTerms(body, v.haulId);
    else if (v.kind === 'run') this.renderRun(body, v.haulId);
    else if (v.kind === 'receipt') this.renderReceipt(body, v.haulId);
    else this.renderReceipts(body);
    requestAnimationFrame(() => this.fitIntoView());
    if (this.status) {
      const s = document.createElement('div');
      s.dataset.portStatus = '1';
      s.style.cssText = 'font:12px var(--ax-font);color:var(--ax-soft);margin-top:4px';
      s.textContent = this.status;
      body.appendChild(s);
    }
  }

  private lines(body: HTMLElement, lines: readonly TermsLine[]): void {
    for (const l of lines) {
      const r = document.createElement('div');
      r.style.cssText = css.row;
      r.dataset.term = l.label;
      const a = document.createElement('div'); a.style.cssText = css.label; a.textContent = l.label;
      const b = document.createElement('div'); b.style.cssText = l.warn ? css.warn : css.text; b.textContent = (l.warn ? '⚠ ' : '') + l.text;
      r.append(a, b);
      body.appendChild(r);
    }
  }
  private button(text: string, onClick: () => void, primary = false, data = ''): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = text;
    b.style.cssText = primary ? css.primary : css.btn;
    if (data) b.dataset.portAct = data;
    b.onclick = onClick;
    return b;
  }
  private input(value: string, placeholder: string, width: string, onInput: (v: string) => void, data = ''): HTMLInputElement {
    const i = document.createElement('input');
    i.value = value; i.placeholder = placeholder;
    i.style.cssText = css.input + `;width:${width}`;
    if (data) i.dataset.portField = data;
    i.oninput = () => { onInput(i.value); this.renderPreviewOnly(); };
    return i;
  }
  private previewEl: HTMLElement | null = null;
  private resizeHooked = false;
  /** Keep the whole window on screen (a remembered position from a bigger screen, a 720p window at 200 %). */
  private fitIntoView(): void {
    const root = this.win?.root;
    if (!root || !this.win?.isOpen()) return;
    const r = root.getBoundingClientRect();
    const w = Math.min(r.width, innerWidth - 16), h = Math.min(r.height, innerHeight - 16);
    if (r.width > w) root.style.width = `${w}px`;
    if (r.height > h) root.style.height = `${h}px`;
    const left = Math.max(8, Math.min(r.left, innerWidth - w - 8)), top = Math.max(8, Math.min(r.top, innerHeight - h - 8));
    if (Math.abs(left - r.left) > 0.5) root.style.left = `${left}px`;
    if (Math.abs(top - r.top) > 0.5) root.style.top = `${top}px`;
  }
  private renderPreviewOnly(): void {
    const v = this.view;
    if (v.kind !== 'terms' || !this.previewEl) return;
    const h = run(v.haulId);
    if (!h) return;
    this.previewEl.textContent = this.previewText(h);
    // The terms' own Crew split / Relay lines follow the fields too — one set of numbers on screen.
    const cs = this.crewShares();
    if (cs.problem) return;
    const lines = termsLines(termsInputFor(h, cs.shares.map((s) => ({ key: s.key, name: s.name, bps: s.bps })), this.partner.trim()));
    for (const label of ['Crew split', 'Relay']) {
      const el = this.win?.root.querySelector(`[data-term="${label}"]`)?.lastChild as HTMLElement | null;
      const l = lines.find((x) => x.label === label);
      if (el && l) el.textContent = l.text;
    }
  }
  /** The crew as typed → shares by key, or the first problem. */
  private crewShares(): { shares: Array<{ key: string; name: string; bps: number; id: Identity }>; problem: string | null; partner: Identity } {
    const out: Array<{ key: string; name: string; bps: number; id: Identity }> = [];
    for (const c of this.crew) {
      if (!c.name.trim() && !c.pct.trim()) continue;
      const id = pilotByName(c.name);
      if (!id) return { shares: out, problem: `No pilot named "${c.name.trim()}"`, partner: ZERO() };
      const b = bpsOf(c.pct);
      if (b === null) return { shares: out, problem: `${c.name.trim()}'s share must be 0.01 … 100 %`, partner: ZERO() };
      out.push({ key: id.toHexString(), name: c.name.trim(), bps: b, id });
    }
    let partner = ZERO();
    if (this.partner.trim()) {
      const p = pilotByName(this.partner);
      if (!p) return { shares: out, problem: `No pilot named "${this.partner.trim()}"`, partner: ZERO() };
      partner = p;
    }
    const mine = me();
    const problem = mine ? crewTermsProblem(mine.toHexString(), out.map((s) => ({ key: s.key, bps: s.bps }) as ShareIn), isZero(partner) ? '' : partner.toHexString()) : 'Not connected';
    return { shares: out, problem, partner };
  }
  private previewText(h: HaulRow): string {
    const cs = this.crewShares();
    if (cs.problem) return `⚠ ${cs.problem}`;
    const mine = me();
    const pv = splitPreview(h.rewardSov, mine?.toHexString() ?? 'you', cs.shares.map((s) => ({ key: s.key, bps: s.bps })));
    const names = new Map(cs.shares.map((s) => [s.key, s.name] as [string, string]));
    return 'Delivery pays: ' + pv.map((p, i) => `${i === 0 ? 'you (captain)' : names.get(p.key)} ${pct(p.bps)} → ${sov(p.sov)}`).join(' · ');
  }

  private renderTerms(body: HTMLElement, haulId: bigint): void {
    this.win?.setTitle('Port run — the terms');
    const h = run(haulId);
    if (!h || h.state !== 'offered') {
      const d = document.createElement('div'); d.style.cssText = css.text;
      d.textContent = h ? `That run is ${h.state} — it is no longer on offer.` : 'That run is no longer on offer.';
      body.appendChild(d);
      return;
    }
    const cs = this.crewShares();
    const head = document.createElement('div'); head.style.cssText = css.head;
    head.textContent = `${h.direction === 'up' ? 'UP' : 'DOWN'} RUN #${h.haulId} — READ BEFORE YOU TAKE IT`;
    body.appendChild(head);
    this.lines(body, termsLines(termsInputFor(h, cs.shares.map((s) => ({ key: s.key, name: s.name, bps: s.bps })), cs.problem ? '' : (this.partner.trim() || ''))));
    const ch = document.createElement('div'); ch.style.cssText = css.head; ch.textContent = 'CREW (optional) — shares of the one reward';
    body.appendChild(ch);
    this.crew.forEach((c, i) => {
      const r = document.createElement('div'); r.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;align-items:center';
      r.append(
        this.input(c.name, 'pilot name', '160px', (v) => { this.crew[i].name = v; }, `crew-name-${i}`),
        this.input(c.pct, 'share %', '80px', (v) => { this.crew[i].pct = v; }, `crew-pct-${i}`),
        this.button('✕ Remove', () => { this.crew.splice(i, 1); this.render(); }),
      );
      body.appendChild(r);
    });
    const add = document.createElement('div'); add.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;align-items:center';
    if (this.crew.length < 3) add.appendChild(this.button('＋ Add crew', () => { this.crew.push({ name: '', pct: '' }); this.render(); }, false, 'add-crew'));
    for (const c of crewCandidates()) {
      if (this.crew.some((x) => x.name.toLowerCase() === c.name.toLowerCase()) || this.crew.length >= 3) continue;
      add.appendChild(this.button(`＋ ${c.name} (${c.why})`, () => { this.crew.push({ name: c.name, pct: '40' }); this.render(); }));
    }
    body.appendChild(add);
    const pr = document.createElement('div'); pr.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;align-items:center';
    const pl = document.createElement('span'); pl.style.cssText = css.label; pl.textContent = 'Relay partner (rescue)';
    pr.append(pl, this.input(this.partner, 'pilot name — may take the run over once', '260px', (v) => { this.partner = v; }, 'partner'));
    body.appendChild(pr);
    this.previewEl = document.createElement('div');
    this.previewEl.dataset.portPreview = '1';
    this.previewEl.style.cssText = css.text;
    this.previewEl.textContent = this.previewText(h);
    body.appendChild(this.previewEl);
    const acts = document.createElement('div'); acts.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;margin-top:6px';
    acts.append(
      this.button('✔ Accept on these terms', () => { void this.accept(h); }, true, 'accept'),
      this.button('✕ Not now', () => this.close(), false, 'cancel'),
    );
    body.appendChild(acts);
  }
  private async accept(h: HaulRow): Promise<void> {
    const cs = this.crewShares();
    if (cs.problem) { this.say(cs.problem, 'error'); return; }
    const r = R();
    const solo = cs.shares.length === 0 && isZero(cs.partner);
    const ok = await this.call(
      solo ? r?.portAccept({ haulId: h.haulId }) : r?.portAcceptTerms({ haulId: h.haulId, crew: cs.shares.map((s) => ({ who: s.id, shareBps: s.bps })), relayPartner: cs.partner }),
      h.direction === 'up' ? `The port's ${h.qty} u are aboard — land them docked at the yard` : `The port's ${h.qty} u are aboard — land them at the port city (E at the spaceport)`,
    );
    if (ok) { this.view = { kind: 'run', haulId: h.haulId }; this.render(); }
  }

  private renderRun(body: HTMLElement, haulId: bigint): void {
    this.win?.setTitle('Port run — in your care');
    const h = run(haulId);
    const t = termsOf(haulId);
    if (!h) { body.appendChild(Object.assign(document.createElement('div'), { textContent: 'No such run.' })); return; }
    const role = relayRole(h);
    const head = document.createElement('div'); head.style.cssText = css.head;
    head.textContent = `RUN #${h.haulId} — ${h.state.toUpperCase()} · custodian ${nameOf(h.hauler)}`;
    body.appendChild(head);
    const left = Math.max(0, Math.round((Number(h.deadlineAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 60_000));
    const lines: TermsLine[] = [
      { label: 'Owner', text: "the port — the crates stay the port's" },
      { label: 'Custodian', text: `${nameOf(h.hauler)}${t && t.custodianRev > 0 ? ` (relayed once, from ${nameOf(t.relayFrom)})` : ''}` },
      { label: 'Deadline', text: `${left} min left`, warn: left < 30 && h.state === 'aboard' },
      { label: 'Reward held', text: `${sov(h.rewardSov)}` },
    ];
    if (t) {
      const pv = splitPreview(h.rewardSov, t.captain.toHexString(), crewOf(t.splitJson));
      lines.push({ label: `Split (rev ${t.splitRev})`, text: pv.map((p, i) => `${i === 0 ? `${nameOf(t.captain)} (captain)` : nameOf(Identity.fromString(p.key))} ${pct(p.bps)} → ${sov(p.sov)}`).join(' · ') });
      lines.push({ label: 'Relay', text: t.custodianRev > 0 ? 'used — no further substitution' : !isZero(t.relayPartner) ? `${nameOf(t.relayPartner)} is pre-authorised (once)` : 'no partner named' });
      if (!isZero(t.offerTo) && Number(t.offerExpiresAt.microsSinceUnixEpoch / 1000n) > Date.now()) lines.push({ label: 'Live offer', text: `to ${nameOf(t.offerTo)}${t.offerBps ? `, ceding ${pct(t.offerBps)} of the captain's share` : ''}` });
    } else lines.push({ label: 'Terms', text: 'taken before crew terms — paid whole to whoever lands it; no relay' });
    this.lines(body, lines);
    const acts = document.createElement('div'); acts.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:6px';
    if (role === 'captain') {
      acts.append(
        this.input(this.offerName, 'hand it to pilot…', '170px', (v) => { this.offerName = v; }, 'offer-name'),
        this.input(this.offerPct, 'cede % of your share', '120px', (v) => { this.offerPct = v; }, 'offer-pct'),
        this.button('⇄ Offer the run', () => {
          const to = pilotByName(this.offerName);
          if (!to) { this.say(`No pilot named "${this.offerName.trim()}"`, 'error'); return; }
          const b = this.offerPct.trim() === '' || this.offerPct.trim() === '0' ? 0 : bpsOf(this.offerPct);
          if (b === null) { this.say('The share you cede must be 0 … 100 %', 'error'); return; }
          void this.call(R()?.portRelayOffer({ haulId, to, shareBps: b }), `Offered to ${this.offerName.trim()} for ${Math.round(RELAY_OFFER_S / 60)} min — they take it over beside your hull`);
        }, false, 'offer'),
        this.button('⊘ Revoke relay', () => { void this.call(R()?.portRelayRevoke({ haulId }), 'No one may take the run over now'); }, false, 'revoke'),
      );
    } else if (role === 'receiver') {
      const holding = this.holdUntil > Date.now();
      acts.append(this.button(holding ? `⏳ Holding… ${Math.ceil((this.holdUntil - Date.now()) / 1000)} s` : '⇄ Take over the run', () => { void this.takeOver(haulId); }, true, 'take-over'));
      const n = document.createElement('span'); n.style.cssText = css.label;
      n.textContent = `docked at the custodian's station: at once · in flight: within 300 m, hold ${RELAY_HOLD_S} s matching speed`;
      acts.appendChild(n);
    }
    body.appendChild(acts);
  }
  /** The receiver's take-over: docked it completes at once; in flight the first call begins the hold and this calls
   *  again after it (retrying while the server says "hold station"). */
  async takeOver(haulId: bigint): Promise<boolean> {
    const r = R();
    if (!r) return false;
    try { await r.portRelay({ haulId }); } catch (e) { this.say(String((e as Error)?.message ?? e), 'error'); return false; }
    const h = run(haulId);
    const mine = me();
    if (h && mine && h.hauler.isEqual(mine)) { this.say(`The run is in your hold — the same ${h.qty} u, the same deadline`, 'success'); return true; }
    this.holdUntil = Date.now() + RELAY_HOLD_S * 1000 + 400;
    this.say(`Hold station beside the hull — ${RELAY_HOLD_S} s, match its speed`, 'info');
    for (let i = 0; i < 12; i++) {
      await new Promise((res) => setTimeout(res, i === 0 ? RELAY_HOLD_S * 1000 + 400 : 1000));
      try {
        await r.portRelay({ haulId });
        const h2 = run(haulId);
        if (h2 && mine && h2.hauler.isEqual(mine)) { this.holdUntil = 0; this.say(`The run is in your hold — the same ${h2.qty} u, the same deadline`, 'success'); return true; }
      } catch (e) {
        const m = String((e as Error)?.message ?? e);
        if (!/Hold station/.test(m)) { this.holdUntil = 0; this.say(m, 'error'); return false; }
      }
    }
    this.holdUntil = 0;
    return false;
  }

  private renderReceipt(body: HTMLElement, haulId: bigint): void {
    this.win?.setTitle('Port run — the receipt');
    const r = receipts().find((x) => x.haulId === haulId);
    if (!r) { const d = document.createElement('div'); d.style.cssText = css.text; d.textContent = 'No receipt yet — a receipt is written when the run is landed.'; body.appendChild(d); return; }
    const head = document.createElement('div'); head.style.cssText = css.head; head.textContent = `RECEIPT — RUN #${r.haulId}`;
    body.appendChild(head);
    for (const s of receiptLines(receiptView(r))) {
      const row = document.createElement('div'); row.style.cssText = css.row; row.dataset.receiptStep = s.step; row.dataset.receiptState = s.state;
      const a = document.createElement('div'); a.style.cssText = css.label; a.textContent = `${s.state === 'done' ? '✓' : s.state === 'wait' ? '…' : '■'} ${s.step}`;
      const b = document.createElement('div'); b.style.cssText = s.state === 'done' ? css.text : css.warn; b.textContent = s.text;
      row.append(a, b);
      body.appendChild(row);
    }
    const back = document.createElement('div'); back.style.cssText = 'margin-top:6px';
    back.appendChild(this.button('← All my receipts', () => { this.view = { kind: 'receipts' }; this.render(); }));
    body.appendChild(back);
  }
  private renderReceipts(body: HTMLElement): void {
    this.win?.setTitle('Port run — receipts');
    const list = myReceipts();
    const head = document.createElement('div'); head.style.cssText = css.head; head.textContent = `MY PORT RECEIPTS — ${list.length}`;
    body.appendChild(head);
    if (!list.length) { const d = document.createElement('div'); d.style.cssText = css.text; d.textContent = 'No run of yours has been landed yet.'; body.appendChild(d); }
    for (const r of list.slice(0, 12)) {
      const row = document.createElement('div'); row.style.cssText = 'display:flex;gap:8px;align-items:center;flex-wrap:wrap';
      const t = document.createElement('div'); t.style.cssText = css.text + ';flex:1';
      t.textContent = `#${r.haulId} · ${r.direction.toUpperCase()} · ${r.qty} u · ${r.stage}${r.wait ? ` (${r.wait.replace(/_/g, ' ')})` : ''} · ${sov(r.rewardSov)}`;
      row.append(t, this.button('Receipt…', () => { this.view = { kind: 'receipt', haulId: r.haulId }; this.render(); }, false, `receipt-${r.haulId}`));
      body.appendChild(row);
    }
  }
}

/** The one port window (globalThis-backed: the dev graph can load a module twice). */
export function portWindow(): PortWindow {
  const g = globalThis as unknown as { __atlasPortWindow?: PortWindow };
  if (!g.__atlasPortWindow) g.__atlasPortWindow = new PortWindow();
  return g.__atlasPortWindow;
}
